import { PortfolioSettingsModel } from "../../../../../portfolio-info/types/PortfolioSettingsModel";
import type { MarketDataDto } from "../../../../../../shared/types/MarketDataDto";
import { ParameterType } from "../../../../../options-table/types/ParameterType";
import { BaseChartDataSeriesService } from "../../common/BaseChartDataSeriesService";
import { PortfolioProfile } from "../../../../../../shared/types/PortfolioProfile";

class ChartDataSeriesService extends BaseChartDataSeriesService {
    private readonly deltaValuePrecision = 4;
    private readonly gammaValuePrecision = 6;
    private readonly vegaValuePrecision = 2;
    private readonly thetaValuePrecision = 2;
    private readonly rhoValuePrecision = 2;

    getSelectedChartSeries = (
        marketDataDto: MarketDataDto,
        portfolioProfile: PortfolioProfile,
        comparedPortfolioProfile: PortfolioProfile,
        portfolioSettings: PortfolioSettingsModel
    ): any[] => {
        const series: any[] = [];

        let seriesName: string;
        let valuePrecision: number;

        switch (portfolioSettings.selectedOptionParameterType) {
            case ParameterType.Delta:
                seriesName = 'delta';
                valuePrecision = this.deltaValuePrecision;
                break;

            case ParameterType.Gamma:
                seriesName = 'gamma';
                valuePrecision = this.gammaValuePrecision;
                break;

            case ParameterType.Vega:
                seriesName = 'vega';
                valuePrecision = this.vegaValuePrecision;
                break;

            case ParameterType.Theta:
                seriesName = 'theta';
                valuePrecision = this.thetaValuePrecision;
                break;

            case ParameterType.Rho:
                seriesName = 'rho';
                valuePrecision = this.rhoValuePrecision;
                break;
                
            default:
                seriesName = 'unknown';
                valuePrecision = 0;
                break;
        }

        this.getChartSeries(marketDataDto, portfolioProfile, comparedPortfolioProfile, portfolioSettings, seriesName, valuePrecision).map(x => series.push(x));

        return series;
    }

    private getChartSeries = (
        marketDataDto: MarketDataDto,
        portfolioProfile: PortfolioProfile,
        comparedPortfolioProfile: PortfolioProfile,
        portfolioSettings: PortfolioSettingsModel,
        seriesName: string,
        valuePrecision: number
    ): any[] => {
        const series: any[] = [];

        series.push(this.getCurrentSeries(marketDataDto, portfolioProfile, comparedPortfolioProfile, portfolioSettings, seriesName, valuePrecision));
        series.push(this.getCustomSeries(seriesName, valuePrecision));
        series.push(this.getExpirationSeries(seriesName, valuePrecision));

        if (portfolioSettings.useComparedPortfolio) {
            series.push(this.getComparedCurrentSeries(seriesName, valuePrecision));
            series.push(this.getComparedCustomSeries(seriesName, valuePrecision));
            series.push(this.getComparedExpirationSeries(seriesName, valuePrecision));
        }

        return series;
    }

    private getCurrentSeries = (
        marketDataDto: MarketDataDto,
        portfolioProfile: PortfolioProfile,
        comparedPortfolioProfile: PortfolioProfile,
        portfolioSettings: PortfolioSettingsModel,
        seriesName: string,
        valuePrecision: number
    ) => {
        return {...
            this.getSeries(`Current ${seriesName}`, this.style.selectedCurrentLineColour,
                this.style.selectedCurrentLineType, this.style.selectedLineWidth, valuePrecision),
            markLine: this.getMarkLines(marketDataDto, portfolioProfile, comparedPortfolioProfile, portfolioSettings),
            markArea: this.getMarkAreas(portfolioSettings),
        };
    }

    private getComparedCurrentSeries = (seriesName: string, valuePrecision: number) =>
        this.getSeries(`Compared current ${seriesName}`,this.style.comparedCurrentLineColour,
            this.style.comparedCurrentLineType, this.style.comparedLineWidth, valuePrecision);

    private getCustomSeries = (seriesName: string, valuePrecision: number) =>
        this.getSeries(`Custom ${seriesName}`, this.style.selectedCustomLineColour,
            this.style.selectedCustomLineType, this.style.selectedLineWidth, valuePrecision);

    private getComparedCustomSeries = (seriesName: string, valuePrecision: number) =>
        this.getSeries(`Compared custom ${seriesName}`, this.style.comparedCustomLineColour,
            this.style.comparedCustomLineType, this.style.comparedLineWidth, valuePrecision);

    private getExpirationSeries = (seriesName: string, valuePrecision: number) =>
        this.getSeries(`Expiration ${seriesName}`, this.style.selectedExpirationLineColour,
            this.style.selectedExpirationLineType, this.style.selectedLineWidth, valuePrecision);

    private getComparedExpirationSeries = (seriesName: string, valuePrecision: number) =>
        this.getSeries(`Compared expiration ${seriesName}`, this.style.comparedExpirationLineColour,
            this.style.comparedExpirationLineType, this.style.comparedLineWidth, valuePrecision);
}

export const chartDataSeriesService = new ChartDataSeriesService();

